import React from 'react';
import { useParams } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import TopBar from '../components/TopBar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/ui/breadcrumb';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const ShowcasePage = () => {
  const { id } = useParams();
  const { showcases } = useData();
  const showcase = showcases.find(s => s.id === id);

  if (!showcase) return <div>Showcase not found</div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href="/">Home</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink href="/invest">Invest</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{showcase.title}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-8">
          <div className="flex flex-wrap gap-2 mb-3">
            {showcase.openForCoInvestment && (
              <Badge style={{ backgroundColor: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-700))' }}>
                ◆ Co-investment
              </Badge>
            )}
            {showcase.openForCollaboration && (
              <Badge style={{ backgroundColor: 'hsl(var(--mint))', color: 'hsl(var(--ink))' }}>
                ● Collaboration
              </Badge>
            )}
            <Badge variant="secondary">{showcase.stage}</Badge>
          </div>
          <h1 className="mb-3">{showcase.title}</h1>
          <p className="text-lg" style={{ color: 'hsl(var(--ink-muted))' }}>{showcase.location} · {showcase.region}</p>
        </div>

        {/* Hero Image Placeholder */}
        <div className="w-full h-64 rounded-2xl mb-8 flex items-center justify-center" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
          <span className="text-6xl">🌍</span>
        </div>

        {/* Facts Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="text-xs mb-1" style={{ color: 'hsl(var(--ink-muted))' }}>Capacity</div>
            <div className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{showcase.capacity} {showcase.capacityUnit}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs mb-1" style={{ color: 'hsl(var(--ink-muted))' }}>Commissioned</div>
            <div className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{showcase.commissionedYear}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs mb-1" style={{ color: 'hsl(var(--ink-muted))' }}>Offtake</div>
            <div className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '16px' }}>{showcase.offtakeType}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs mb-1" style={{ color: 'hsl(var(--ink-muted))' }}>GOTF Role</div>
            <div className="text-sm font-semibold">{showcase.gotfRole}</div>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            <Card className="p-6">
              <h3 className="mb-4">Overview</h3>
              <p>{showcase.overview}</p>
            </Card>

            <Card className="p-6">
              <h3 className="mb-4">What Worked</h3>
              <p>{showcase.whatWorked}</p>
            </Card>

            <Card className="p-6">
              <h3 className="mb-4">Lessons Learned</h3>
              <p>{showcase.lessons}</p>
            </Card>

            {showcase.contacts && showcase.contacts.length > 0 && (
              <Card className="p-6">
                <h3 className="mb-4">Contacts</h3>
                <div className="space-y-3">
                  {showcase.contacts.map((contact, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(var(--primary))', color: 'white', fontWeight: '600' }}>
                        {contact.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-medium">{contact.name}</div>
                        <div className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>{contact.role}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          <aside className="space-y-6">
            {showcase.openForCoInvestment && (
              <Card className="p-5" style={{ borderColor: 'hsl(var(--primary))' }}>
                <h4 className="mb-4">Interested in co-investing?</h4>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="name">Your Name</Label>
                    <Input id="name" placeholder="Full name" />
                  </div>
                  <div>
                    <Label htmlFor="org">Organization</Label>
                    <Input id="org" placeholder="Company name" />
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea id="message" placeholder="Tell us about your interest..." rows={3} />
                  </div>
                  <Button className="w-full">Submit Inquiry</Button>
                </div>
                <p className="text-xs mt-3" style={{ color: 'hsl(var(--ink-muted))' }}>We'll connect you with the project team within 48 hours.</p>
              </Card>
            )}

            <Card className="p-5">
              <h4 className="text-sm font-semibold mb-3">Technologies</h4>
              <div className="flex flex-wrap gap-2">
                {showcase.technologies.map(tech => (
                  <span key={tech} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>{tech}</span>
                ))}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ShowcasePage;
